import { useState } from 'react'
import { X } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { fieldLabel, fieldWrapper, inputBase } from '@/lib/form-styles'
import { useTerminology } from '@/lib/useTerminology'
import { createProjectFromCalculation, type CalcProjectResult } from '../api/calculation-project.api'
import type { Calculation } from '../types/calculation.types'

/**
 * AP-37 4.15 — **Beauftragt: {Projekt} anlegen.** Ein Anlegen-Dialog (AP-36
 * Fall 2) nur mit dem, was zum Anlegen nötig ist: Bezeichnung und Bestellzeichen.
 * Danach öffnet sich das Projekt im Reiter Positionen mit dem Einfügen-Dialog —
 * dort entscheidet der Benutzer, wie die Kalkulationspositionen ankommen.
 */
export function ProjektAnlegenDialog({ kalkulation, onClose, onDone }: { kalkulation: Calculation; onClose: () => void; onDone: (r: CalcProjectResult) => void }) {
  const terms = useTerminology()
  const { hasModule } = useAuthStore()
  const [name, setName] = useState(kalkulation.name)
  const [bestellzeichen, setBestellzeichen] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const ohneKunde = !kalkulation.clientId

  const anlegen = async () => {
    setLaeuft(true)
    setFehler(null)
    try {
      onDone(await createProjectFromCalculation(kalkulation.id, { name: name.trim() || undefined, customerOrderRef: bestellzeichen.trim() || undefined }))
    } catch (e) {
      setFehler(apiErrorText(e, `${terms.project} ließ sich nicht anlegen.`))
      setLaeuft(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg border border-border p-6 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-semibold text-primary">{terms.project} anlegen</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
        {fehler && <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}
        {ohneKunde ? (
          <p className="text-sm">
            Die Kalkulation hat noch keinen Kunden, nur einen Interessenten. Wähle in den Kalkulationsdaten den Kunden — oder erstelle zuerst das Angebot, das ihn anlegt.
          </p>
        ) : (
          <div className="space-y-4">
            <div className={fieldWrapper}>
              <label className={fieldLabel} htmlFor="proj-name">
                {terms.name}
              </label>
              <input id="proj-name" className={inputBase} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className={fieldWrapper}>
              <label className={fieldLabel} htmlFor="proj-ref">
                Bestellzeichen des Kunden
              </label>
              <input id="proj-ref" className={inputBase} value={bestellzeichen} onChange={(e) => setBestellzeichen(e.target.value)} />
            </div>
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              <li>Kunde, Ansprechpartner und Bauvorhaben kommen aus der Kalkulation.</li>
              <li>
                Alle Positionen liegen danach in der Zwischenablage; im {terms.project} entscheidest du beim Einfügen, ob je Position eine {terms.position} entsteht oder
                mehrere zusammengefasst werden.
              </li>
              {kalkulation.hasLv &&
                (hasModule('PROJECT_MGMT.IMPORT') ? (
                  <li>Das LV hängt mit den Preisen der Kalkulation am {terms.project}.</li>
                ) : (
                  <li>Das LV kommt nicht mit — dafür fehlt der Baustein „LV im {terms.project}".</li>
                ))}
            </ul>
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50" disabled={ohneKunde || laeuft} onClick={() => void anlegen()}>
            Anlegen
          </button>
        </div>
      </div>
    </div>
  )
}
